import { notYetEndedSql } from "./dates";

// dog-size-gate-everywhere — the rules the booking gate, the importer and the
// report share. ONE module, because the first version of that story found the
// gate on POST only and assumed the other doors were open; the audit found them
// shut by three different copies of "big or little". Three copies is how one
// of them drifts to accept `medium`.

/** The only two sizes capacity can count. Everything else — `unknown`, `mix`,
 *  `house`, `medium`, null — is "no usable size". */
export const USABLE_SIZES = ["big", "little"] as const;
export type UsableSize = (typeof USABLE_SIZES)[number];

export function isUsableSize(size: string | null | undefined): size is UsableSize {
  return size === "big" || size === "little";
}

/**
 * The invariant (AC3 / AC3a): no booking at `unconfirmed` or beyond carries an
 * unsized dog. A customer's `requested` booking is exempt — customers never
 * see size, the calendar counts the dog as big, and Accept asks Patricia for
 * the size before the booking can leave `requested`. A cancellation is not
 * gated either: refusing to cancel a booking over a dog's size would be the
 * gate getting in the way of the one thing nobody should be stopped from doing.
 */
export const SIZE_GATED_STATUSES = ["unconfirmed", "confirmed", "arrived", "collected"] as const;

export function sizeGateApplies(status: string): boolean {
  return (SIZE_GATED_STATUSES as readonly string[]).includes(status);
}

/**
 * The one sentence every door says — the server's refusal and the booking
 * form's own pre-save check. SHORT ON PURPOSE: the toast it lands in already
 * opens "Unable to save —", so it names the dogs and the fault and stops
 * (Freddi, 2026-09-11; and 2026-10-08 on seeing the long form on the desk).
 */
export function unsizedMessage(names: readonly string[]): string {
  const list = names.join(" and ");
  return `${list} ${names.length === 1 ? "has" : "have"} no size.`;
}

/**
 * The importer's rule (AC1 / AC1b). A dog the sheet does not size, ON a
 * booking in the sheet, is written `big` with a warning — it must stay visible
 * to the calendar, and `backfill-dog-size.ts` corrects it by area afterwards.
 * One on NO booking is written `unknown`: there is nothing to count and
 * nothing to invent. Reversed from the story's first draft, which made every
 * such dog `unknown` and would have attached every imported booking to an
 * invisible dog.
 */
export function importSize(
  sheetSize: string | null | undefined,
  onBooking: boolean,
  who: { owner?: string; dog?: string } = {},
): { size: UsableSize | "unknown"; warning: string | null } {
  if (isUsableSize(sheetSize)) return { size: sheetSize, warning: null };
  if (!onBooking) return { size: "unknown", warning: null };
  const name = [who.owner, who.dog].filter(Boolean).join(" · ") || "a dog";
  return {
    size: "big",
    warning: `${name}: no usable size on the sheet; written as big so its bookings count — set the real size on the desk, or run backfill-dog-size`,
  };
}

/** SQL predicate: this dogs-table alias has no usable size. */
export function unusableSizeSql(alias: string): string {
  const p = alias ? `${alias}.` : "";
  return `(${p}size IS NULL OR ${p}size NOT IN ('big','little'))`;
}

/**
 * Report section 1 (AC4): every not-yet-ended, live booking carrying a dog
 * with no usable size. Archived dogs are left out, as section 2 leaves them
 * out: archiving a household archives its dogs and keeps its bookings, and a
 * dog nobody can open must not hold the calendar gate shut (review on #239). `today` is a SQL expression, e.g. `'2026-10-07'::date`
 * or `$1::date` — the caller decides the clock.
 */
export function unsizedOnUpcomingSql(today: string): string {
  return `SELECT b.id AS booking_id, b.check_in::text AS check_in, b.check_out::text AS check_out, b.status,
                 COALESCE((SELECT oc.name FROM owner_contacts oc WHERE oc.owner_id = b.owner_id ORDER BY oc.sort_order LIMIT 1), b.owner_id) AS owner,
                 d.name AS dog, COALESCE(d.size, '') AS size
            FROM booking_dogs bd
            JOIN dogs d ON d.id = bd.dog_id
            JOIN bookings b ON b.id = bd.booking_id
           WHERE ${notYetEndedSql("b", today)} AND d.archived_at IS NULL AND ${unusableSizeSql("d")}
           ORDER BY b.check_in, b.id, d.name`;
}

/** Report section 2 (AC4): every bad stored size, counted. */
export function badSizeCountsSql(): string {
  return `SELECT COALESCE(size, '') AS size, count(*)::int AS n
            FROM dogs
           WHERE archived_at IS NULL AND ${unusableSizeSql("")}
           GROUP BY size ORDER BY size`;
}

export interface UnsizedUpcomingRow {
  booking_id: string; check_in: string; check_out: string; owner: string; dog: string; size: string;
}

/** The report's text and exit code, from the two queries' rows. Pure, so it is testable. */
export function summariseDogSizes(
  upcoming: readonly UnsizedUpcomingRow[],
  rest: readonly { size: string; n: number }[],
): { text: string; exitCode: 0 | 1 } {
  const lines: string[] = [];
  lines.push("1. Unsized dogs on not-yet-ended bookings — these block the calendar");
  if (upcoming.length === 0) lines.push("   none");
  for (const r of upcoming) {
    lines.push(`   ${r.booking_id}  ${r.check_in} → ${r.check_out}  ${r.owner}  ${r.dog}  [${r.size || "null"}]`);
  }
  lines.push("");
  lines.push("2. Every stored size outside big and little");
  if (rest.length === 0) lines.push("   none");
  for (const r of rest) lines.push(`   ${(r.size || "null").padEnd(10)} ${r.n}`);
  return { text: lines.join("\n"), exitCode: upcoming.length === 0 ? 0 : 1 };
}
